// apps/nos/client/src/hooks/useAmplitudeUser.ts
import { useEffect } from "react";
import { useAuth, useOrganization, useUser } from "@clerk/react";
import { amplitudeEnabled } from "@/lib/amplitudeInit";
import { clearUserContext, logEvent, setUserContext } from "@/lib/eventLogger";

// Per-browser-session "app open" gate. sessionStorage clears when the tab
// closes, so this fires once per browser-tab session per signed-in user.
const SESSION_STARTED_FLAG = "palate_amplitude_session_started_fired";

/**
 * Syncs the current Clerk identity into Amplitude. Mounted once inside
 * ProtectedShell — runs on sign-in, on user/org change, and on sign-out.
 *
 * Mirrors useSentryUser exactly so the two pipelines stay in lock-step.
 *
 * Note: user_profile_id (accounts.user_profile.id) is server-side only; for
 * client-fired events the join key is clerk_user_id + org_id.
 */
export function useAmplitudeUser(): void {
  const { isLoaded: authLoaded, isSignedIn, userId } = useAuth();
  const { user } = useUser();
  const { organization } = useOrganization();

  useEffect(() => {
    if (!amplitudeEnabled || !authLoaded) return;

    if (!isSignedIn) {
      clearUserContext();
      return;
    }

    const email = user?.primaryEmailAddress?.emailAddress;
    const name = user?.fullName ?? undefined;
    const username = user?.username ?? undefined;
    const isPersonal = !organization?.id;

    setUserContext({
      clerkUserId: userId ?? undefined,
      orgId: organization?.id ?? undefined,
      orgKind: isPersonal ? "personal" : "shared",
      ...(email ? { email } : {}),
      ...(name ? { name } : {}),
      ...(username ? { username } : {}),
    });

    // Fire app_session_started ONCE per browser-tab session. sessionStorage
    // is cleared automatically when the tab closes, so reopening the app
    // in a fresh tab counts as a new session.
    try {
      if (window.sessionStorage.getItem(SESSION_STARTED_FLAG) !== "1") {
        window.sessionStorage.setItem(SESSION_STARTED_FLAG, "1");
        logEvent("app_session_started");
      }
    } catch {
      // Storage may be unavailable (Safari private mode, etc.) — fire
      // anyway. The cost of a duplicate event per pageload in that case
      // is acceptable.
      logEvent("app_session_started");
    }
  }, [authLoaded, isSignedIn, userId, user, organization]);
}
